import { createApi } from '@reduxjs/toolkit/query/react';
import { buildBaseQueryWithReauth } from './baseQuery';

/**
 * RTK Query API for document and quote downloads
 * Migrated from axios to RTK Query with backend_v2 endpoints
 * Backend routes: /api/v1/b2b/documents and /api/v1/b2b/loan/tools
 */
export const documentDownloadsAPI = createApi({
  reducerPath: 'documentDownloads',
  baseQuery: buildBaseQueryWithReauth({
    prepareHeaders: (headers) => {
      headers.set('Accept', 'application/json, application/pdf, application/octet-stream');
      return headers;
    },
  }),
  endpoints: (builder) => ({
    /**
     * Download a client document by document ID
     * Backend: GET /api/v1/b2b/documents/download?document_id={documentId}
     * Returns: Blob with filename
     */
    downloadClientDocument: builder.mutation({
      query: ({ documentId }) => ({
        url: `/b2b/documents/download`,
        method: 'GET',
        params: { document_id: documentId },
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (response, meta) => {
        const contentDisposition = meta.response?.headers.get('Content-Disposition') || '';
        const contentType = meta.response?.headers.get('Content-Type') || 'application/octet-stream';
        
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        const filename = filenameMatch ? filenameMatch[1] : 'document';

        return {
          blob: response,
          filename,
          contentType,
        };
      },
    }),

    /**
     * Download admin/officer document (alternative endpoint with path param)
     * Backend: GET /api/v1/b2b/documents/download/{document_id}
     * Returns: Blob with filename
     */
    downloadDocument: builder.mutation({
      query: ({ documentId }) => ({
        url: `/b2b/documents/download/${documentId}`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (response, meta) => {
        const contentDisposition = meta.response?.headers.get('Content-Disposition') || '';
        const contentType = meta.response?.headers.get('Content-Type') || 'application/octet-stream';
        
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        const filename = filenameMatch ? filenameMatch[1] : 'document.pdf';

        return {
          blob: response,
          filename,
          contentType,
        };
      },
    }),

    /**
     * Download product quote PDF for loan officer
     * Backend: POST /api/v1/b2b/loan/tools/download_quote/application/{applicationId}
     * Body: Quote data (pricing details, fees, etc.)
     * Returns: PDF blob
     */
    downloadQuote: builder.mutation({
      query: ({ applicationId, data = {} }) => ({
        url: `/b2b/loan/tools/download_quote/application/${applicationId}`,
        method: 'POST',
        body: data,
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (response, meta, { applicationId }) => {
        const contentDisposition = meta.response?.headers.get('Content-Disposition') || '';
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        const filename = filenameMatch ? filenameMatch[1] : `offer-${applicationId}.pdf`;

        return {
          blob: response,
          filename,
          contentType: 'application/pdf',
        };
      },
    }),

    /**
     * Download a B2C consumer document by document ID
     * Backend: GET /api/v1/b2c/loan/documents/{documentId}/download
     * Returns: Blob with filename
     */
    downloadB2cDocument: builder.mutation({
      query: ({ documentId }) => ({
        url: `/b2c/loan/documents/${documentId}/download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (response, meta) => {
        const contentDisposition = meta.response?.headers.get('Content-Disposition') || '';
        const contentType = meta.response?.headers.get('Content-Type') || 'application/octet-stream';

        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        const filename = filenameMatch ? filenameMatch[1] : 'document';

        return {
          blob: response,
          filename,
          contentType,
        };
      },
    }),

    /**
     * Download consumer quote PDF
     * Backend: POST /api/v1/b2b/loan/tools/download_quote_consumer/application/{applicationId}
     * Body: Quote data
     * Returns: PDF blob
     */
    downloadConsumerQuote: builder.mutation({
      query: ({ applicationId, data = {} }) => ({
        url: `/b2b/loan/tools/download_quote_consumer/application/${applicationId}`,
        method: 'POST',
        body: data,
        responseHandler: (response) => response.blob(),
      }),
      transformResponse: (response, meta, { applicationId }) => {
        const contentDisposition = meta.response?.headers.get('Content-Disposition') || '';
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        const filename = filenameMatch ? filenameMatch[1] : `consumer-offer-${applicationId}.pdf`;

        return {
          blob: response,
          filename,
          contentType: 'application/pdf',
        };
      },
    }),
  }),
});

export const {
  useDownloadClientDocumentMutation,
  useDownloadDocumentMutation,
  useDownloadB2cDocumentMutation,
  useDownloadQuoteMutation,
  useDownloadConsumerQuoteMutation,
} = documentDownloadsAPI;

/**
 * Helper function to trigger browser download from blob response
 * @param {Blob} blob - File blob
 * @param {string} filename - Suggested filename
 */
export const triggerBrowserDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Legacy function wrapper for backward compatibility
 * Use the RTK Query hooks instead: useDownloadClientDocumentMutation
 * @deprecated
 */
export const fetchClientDocument = async ({ documentId }) => {
  console.warn('fetchClientDocument is deprecated. Use useDownloadClientDocumentMutation hook instead.');
  // For backward compatibility during migration
  const response = await fetch(`${api}/b2b/documents/download?document_id=${documentId}`, {
    credentials: 'include',
  });
  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition') || '';
  const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : 'document';
  return { blob, filename };
};

/**
 * Legacy function wrapper for B2C consumer document download
 * Use the RTK Query hooks instead: useDownloadB2cDocumentMutation
 * @deprecated
 */
export const fetchB2cDocument = async ({ documentId }) => {
  console.warn('fetchB2cDocument is deprecated. Use useDownloadB2cDocumentMutation hook instead.');
  const response = await fetch(`${api}/b2c/loan/documents/${documentId}/download`, {
    credentials: 'include',
  });
  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition') || '';
  const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : 'document';
  return { blob, filename };
};

/**
 * Legacy function wrapper for backward compatibility
 * Use the RTK Query hooks instead: useDownloadDocumentMutation
 * @deprecated
 */
export const fetchAdminDocument = async ({ documentId }) => {
  console.warn('fetchAdminDocument is deprecated. Use useDownloadDocumentMutation hook instead.');
  const response = await fetch(`${api}/b2b/documents/download/${documentId}`, {
    credentials: 'include',
  });
  const blob = await response.blob();
  const contentDisposition = response.headers.get('Content-Disposition') || '';
  const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch ? filenameMatch[1] : 'document.pdf';
  return { blob, filename };
};

/**
 * Legacy function wrapper for backward compatibility
 * Use the RTK Query hooks instead: useDownloadQuoteMutation
 * @deprecated
 */
export const downloadQuote = async ({ applicationId, data = {} }) => {
  console.warn('downloadQuote function is deprecated. Use useDownloadQuoteMutation hook instead.');
  const response = await fetch(`${api}/b2b/loan/tools/download_quote/application/${applicationId}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return await response.blob();
};

/**
 * Legacy function wrapper for backward compatibility
 * Use the RTK Query hooks instead
 * @deprecated - Quick price endpoint not found in backend_v2, use downloadQuote instead
 */
export const downloadQuickPriceQuote = async ({ data = {} }) => {
  console.warn('downloadQuickPriceQuote is deprecated. Endpoint not found in backend_v2.');
  // Fallback to regular quote download - update based on actual backend endpoint
  return new Blob([], { type: 'application/pdf' });
};

/**
 * Legacy function wrapper for backward compatibility
 * Use the RTK Query hooks instead: useDownloadQuoteMutation
 * @deprecated
 */
export const downloadOffer = async ({ data, applicationId }) => {
  console.warn('downloadOffer is deprecated. Use useDownloadQuoteMutation hook instead.');
  const response = await fetch(`${api}/b2b/loan/tools/download_quote/application/${applicationId}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return await response.blob();
};

/**
 * Legacy function wrapper for backward compatibility
 * Use the RTK Query hooks instead: useDownloadConsumerQuoteMutation
 * @deprecated
 */
export const downloadConsumerOffer = async ({ data, applicationId }) => {
  console.warn('downloadConsumerOffer is deprecated. Use useDownloadConsumerQuoteMutation hook instead.');
  const response = await fetch(`${api}/b2b/loan/tools/download_quote_consumer/application/${applicationId}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return await response.blob();
};
